import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { Link } from "./atoms/Link"
import styled, { css } from "styled-components"
import breakpoint from "styled-components-breakpoint"
import { ButtonResetCSS } from "./atoms/Buttons"
import { VisitorLocaleContext } from "./providers/VisitorLocaleProvider"
import PropTypes from "prop-types"
import { AvocadoBurger } from "./atoms/AvocadoBurger"
import { SIDEBAR_BREAKPOINT } from "./SideBar"
import { SearchButton, SearchInput } from "./search/Elements"
import { mr } from "styled-components-spacing"
import chevronDown from "../images/chevron-down.svg"
import Logo from "./atoms/Logo"
import { useMediaQuery } from "@karbonhq/marketing-shared-utils/hooks/useMediaQuery"
import theme from "../settings/theme"

const Header = styled("header")`
  position: sticky;
  top: 0;
  z-index: 1;
  width: 100%;
  background: white;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: "main";

  ${breakpoint("lg")(
    () => css`
      grid-template-columns: ${props => props.theme.sideBarWidth}px 1fr;
      grid-template-areas: "menu main";
    `
  )}

  grid-column-gap: 40px;
  align-items: center;
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  grid-area: main;
  position: relative;
  min-height: ${props => props.theme.menuHeight}px;
  .main {
    flex: 1;
  }
`

const SubBar = styled.button`
  ${ButtonResetCSS};
  :active {
    outline: 0;
  }
  display: flex;
  align-items: center;
  border-top: 1px solid ${props => props.theme.colors.lightGrey};
  min-height: 48px;

  ${breakpoint(SIDEBAR_BREAKPOINT)(
    () => css`
      display: none;
    `
  )}

  img {
    transform: rotate(-90deg) translate(-1px, 5px);
    transition: transform 150ms linear;
    width: 14px;

    ${props =>
      props.menuOpen &&
      css`
        transform: rotate(0) translate(5px, 1px);
      `}
  }
`

const MenuSearch = ({ searchOpen, setSearchOpen, ...props }) => {
  const close = useCallback(() => {
    setSearchOpen(false)
  }, [setSearchOpen])
  const ref = useRef()

  if (searchOpen) {
    return (
      <div
        {...props}
        ref={ref}
        css={css`
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          z-index: 1; /* to float above burger icon */

          ${breakpoint(SIDEBAR_BREAKPOINT)(css`
            display: none;
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            height: ${props => props.theme.menuHeight}px;
          `}
        >
          <SearchInput
            focusOnMount
            onClear={close}
            css={css`
              flex: 1;
              ${mr(2)};
            `}
          />
          <Link onClick={close}>Cancel</Link>
        </div>
      </div>
    )
  }

  return (
    <SearchButton
      css={css`
        ${breakpoint(SIDEBAR_BREAKPOINT)(css`
          display: none;
        `)}
      `}
      onClick={() => setSearchOpen(true)}
    >
      Search
    </SearchButton>
  )
}

export const Menu = props => {
  const { karbonData } = useContext(VisitorLocaleContext)

  const isBig = useMediaQuery("lg", "min", theme.breakpoints)
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    if (isBig) {
      document.body.style.overflow = "unset"
      props.setMenuOpen(false)
    } else {
      if (props.menuOpen) {
        document.body.style.overflow = "hidden"
      }
      if (!props.menuOpen) {
        document.body.style.overflow = "unset"
      }
    }

    return () => {
      if (window) {
        document.body.style.overflow = "unset"
      }
    }
  }, [props, isBig])

  useEffect(() => {
    if (searchOpen) props.setMenuOpen(false)
  }, [searchOpen, props])

  const toggleMenuOpen = () => {
    props.setMenuOpen(!props.menuOpen)
  }

  return (
    <Header>
      <TopBar>
        <div className="main">
          {!props.backButton && (
            <Logo css={breakpoint(SIDEBAR_BREAKPOINT)`display:none;`} />
          )}

          {props.searchTitle || props.title ? (
            <strong
              css={css`
                font-size: 20px;
                ${breakpoint(
                  "xs",
                  SIDEBAR_BREAKPOINT
                )(
                  css`
                    display: none;
                  `
                )}
              `}
            >
              {props.searchTitle || props.title}
            </strong>
          ) : (
            props.backButton
          )}
        </div>

        <div
          css={css`
            display: block;
            ${breakpoint("sm")(
              css`
                display: block;
              `
            )}
          `}
        >
          <MenuSearch searchOpen={searchOpen} setSearchOpen={setSearchOpen} />

          {!karbonData.isLoggedIn ? (
            <>
              <Link
                href="https://karbonhq.com"
                css={css`
                  display: none;
                  margin-right: 16px;

                  ${breakpoint(SIDEBAR_BREAKPOINT)(
                    css`
                      display: inline-block;
                    `
                  )}
                `}
              >
                Discover Karbon
              </Link>

              <Link
                href="https://app2.karbonhq.com/signin"
                quiet
                css={css`
                  @media (max-width: 400px) {
                    display: none;
                  }
                `}
              >
                Login
              </Link>
            </>
          ) : (
            <Link
              data-e2e={"button__openKarbon"}
              href={karbonData.data.dataCenterUrl}
              css={css`
                @media (max-width: 400px) {
                  display: none;
                }
              `}
            >
              Open Karbon
            </Link>
          )}
        </div>

        <AvocadoBurger
          isOpen={props.menuOpen}
          onClick={toggleMenuOpen}
          css={css`
            ${searchOpen &&
            css`
              display: none;
            `}
            ${breakpoint(SIDEBAR_BREAKPOINT)`display:none`}
          `}
        />
      </TopBar>
      {props.title && !props.hideSubBar && (
        <SubBar onClick={toggleMenuOpen} menuOpen={props.menuOpen}>
          <strong>{props.title}</strong>
          <img src={chevronDown} alt={"toggle main menu"} />
        </SubBar>
      )}
    </Header>
  )
}

Menu.propTypes = {
  hideSubBar: PropTypes.bool,
  title: PropTypes.string,
  searchTitle: PropTypes.string,
  backButton: PropTypes.node,
  menuOpen: PropTypes.bool.isRequired,
  setMenuOpen: PropTypes.func.isRequired,
}
